import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from '../../hooks/useAuth';
import { useNotifications } from '../../context/NotificationContext';
import searchService from '../../api/searchService';
import Field from '../common/Field';
import { validate, isEmail, isStrongPassword, isPhone, minLength, isNotBlank, isPositiveNumber } from '../../utils/validators';

const EMPTY = {
  firstName: '', lastName: '', email: '', password: '', phoneNumber: '',
  technicianTypeId: '', bio: '', yearsOfExperience: '', certificationNumber: '',
  address: '', city: '', state: '', zipCode: '', hourlyRate: '', servicesOffered: '',
};

export default function TechnicianRegistration() {
  const { t } = useTranslation();
  const { registerTechnician } = useAuth();
  const notify = useNotifications();
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    searchService.technicianTypes().then(setTypes).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const req = t('common.required');
    const errs = validate(form, {
      firstName: [[(v) => minLength(v, 2), req]],
      lastName: [[(v) => minLength(v, 2), req]],
      email: [[isEmail, req]],
      password: [[isStrongPassword, t('auth.passwordRule')]],
      phoneNumber: [[isPhone, '+243…']],
      technicianTypeId: [[isNotBlank, req]],
      address: [[isNotBlank, req]],
      city: [[isNotBlank, req]],
      hourlyRate: [[isPositiveNumber, req]],
    });
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    try {
      await registerTechnician({
        ...form,
        state: form.state || form.city,
        zipCode: form.zipCode || '0000',
        yearsOfExperience: form.yearsOfExperience ? Number(form.yearsOfExperience) : null,
        hourlyRate: Number(form.hourlyRate),
        servicesOffered: form.servicesOffered
          ? form.servicesOffered.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
      });
      notify.success(t('auth.technicianPending'));
      navigate('/technician', { replace: true });
    } catch (err) {
      notify.apiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="form-grid">
        <Field label={t('auth.firstName')} value={form.firstName} onChange={set('firstName')} error={errors.firstName} />
        <Field label={t('auth.lastName')} value={form.lastName} onChange={set('lastName')} error={errors.lastName} />
        <Field label={t('auth.email')} type="email" value={form.email} onChange={set('email')} error={errors.email} />
        <Field label={t('auth.password')} type="password" value={form.password} onChange={set('password')} error={errors.password} />
        <Field label={t('auth.phone')} placeholder="+243 …" value={form.phoneNumber} onChange={set('phoneNumber')} error={errors.phoneNumber} />
        <Field as="select" label={t('auth.trade')} value={form.technicianTypeId} onChange={set('technicianTypeId')} error={errors.technicianTypeId}>
          <option value="">—</option>
          {types.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </Field>
        <Field label={t('auth.yearsExperience')} type="number" min="0" value={form.yearsOfExperience} onChange={set('yearsOfExperience')} />
        <Field label={t('auth.hourlyRate')} type="number" min="1" step="0.01" value={form.hourlyRate} onChange={set('hourlyRate')} error={errors.hourlyRate} />
        <Field label={t('auth.certNumber')} value={form.certificationNumber} onChange={set('certificationNumber')} />
        <div />
        <Field label={t('auth.address')} value={form.address} onChange={set('address')} error={errors.address} />
        <Field label={t('auth.city')} value={form.city} onChange={set('city')} error={errors.city} placeholder="Kinshasa" />
        <Field label={`${t('auth.state')} (${t('common.optional')})`} value={form.state} onChange={set('state')} />
        <Field label={`${t('auth.zip')} (${t('common.optional')})`} value={form.zipCode} onChange={set('zipCode')} />
      </div>
      <Field label={t('auth.servicesOffered')} value={form.servicesOffered} onChange={set('servicesOffered')} placeholder="Réparation fuite, Installation chauffe-eau, …" />
      <Field as="textarea" label={t('auth.bio')} value={form.bio} onChange={set('bio')} maxLength={2000} />
      <div className="alert info">{t('auth.technicianPending')}</div>
      <button className="btn block" disabled={busy}>
        {busy ? t('common.loading') : t('auth.submitApplication')}
      </button>
    </form>
  );
}
